import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useAuth, isAdmin } from '@/lib/auth';
import { AlwaysdataConfigPanel } from '@/features/alwaysdata-admin/alwaysdata-config-panel';
import { AlwaysdataMailboxesPanel } from '@/features/alwaysdata-admin/alwaysdata-mailboxes-panel';

type Tab = 'config' | 'mailboxes';

/**
 * Console admin Alwaysdata (admin) :
 *  - onglet « Connexion API » : URL, domaine géré, token (Vault) ;
 *  - onglet « Redirections » : lister / créer / éditer / supprimer les
 *    redirections des boîtes @<domain>.
 */
export function TutorAdminAlwaysdataPage() {
  const { profile, user } = useAuth();
  const [tab, setTab] = useState<Tab>('mailboxes');

  if (!isAdmin(profile, user?.email)) {
    return <p className="p-6 font-sans text-sm">Réservé aux admins.</p>;
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <Link
        to="/tutor/dashboard"
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Admin
      </Link>

      <div className="mb-5">
        <p className="font-display text-accent-500 text-[11px] font-semibold uppercase tracking-[.14em]">
          Emails Alwaysdata
        </p>
        <h1 className="font-display text-ink-900 mt-1 text-2xl font-extrabold tracking-tight">
          Boîtes &amp; redirections
        </h1>
      </div>

      <div className="border-ink-100 mb-5 flex gap-1 border-b">
        <TabButton active={tab === 'mailboxes'} onClick={() => setTab('mailboxes')}>
          Redirections
        </TabButton>
        <TabButton active={tab === 'config'} onClick={() => setTab('config')}>
          Connexion API
        </TabButton>
      </div>

      {tab === 'mailboxes' ? <AlwaysdataMailboxesPanel /> : <AlwaysdataConfigPanel />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px min-h-11 border-b-2 px-3 text-sm font-semibold transition-colors ${
        active
          ? 'border-accent-500 text-ink-900'
          : 'text-ink-400 hover:text-ink-700 border-transparent'
      }`}
    >
      {children}
    </button>
  );
}
